import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { fromUnixTime } from "date-fns/esm";

import PageLoading from "components/loading/PageLoading";
import NotFoundPage from "components/error/NotFoundPage";
import useErrorHandle from "hooks/useErrorHandle";
import { WorkerUsage } from "types/generated";

import UsageWorkersNewContent from "./Content";
import { GET_WORKER_USAGE } from "./gql";
import { FALLBACK_PERCENTILE, REQUESTED_PERCENTILES } from "./constants";
import { WorkerUsageData } from "./types";
import { startOfUtcDay } from "./utils";

const clampToCycle = (date: Date, start: Date, end: Date) =>
  new Date(Math.min(Math.max(date.getTime(), start.getTime()), end.getTime()));

const normalizeWorkerUsage = (usage: WorkerUsage): WorkerUsageData => {
  const cycleStart = fromUnixTime(usage.cycle.start);
  // cycle.end is exclusive, so the last day is the one holding its last billed minute.
  const cycleEnd = startOfUtcDay(fromUnixTime(usage.cycle.end - 1));

  // Half a contract renders exactly like no contract, so the pick happens once,
  // here: the rest of the page sees a single percentile series and never asks
  // which one it is.
  const hasWholeContract =
    usage.contractedLimit != null &&
    usage.contractedPercentile != null &&
    usage.concurrencyAtContractedPercentile != null;

  return {
    cycleStart,
    cycleEnd,
    today: clampToCycle(startOfUtcDay(new Date()), cycleStart, cycleEnd),
    daily: usage.daily.map((point) => ({
      day: fromUnixTime(point.day),
      max: point.max,
      percentileToDateValue: hasWholeContract
        ? (point.concurrencyAtContractedPercentileToDate ?? undefined)
        : point.concurrencyAtPercentilesToDate.find(
            (entry) => entry.percentile === FALLBACK_PERCENTILE
          )?.value,
      burstMinutesUsed: point.burstMinutesUsed ?? undefined,
    })),
    contractedWorkers: usage.contractedLimit ?? undefined,
    contractedPercentile: usage.contractedPercentile ?? undefined,
    percentileToDateValue: usage.concurrencyAtContractedPercentile ?? undefined,
    burst: usage.burst ?? undefined,
    fallbackPercentile: FALLBACK_PERCENTILE,
    fallbackPercentileValue: usage.concurrencyPercentiles.find(
      (entry) => entry.percentile === FALLBACK_PERCENTILE
    )?.value,
  };
};

const UsageWorkersNew = () => {
  const { loading, data, error } = useQuery(GET_WORKER_USAGE, {
    variables: { percentiles: REQUESTED_PERCENTILES },
  });

  const usage = useMemo(
    () => (data?.workerUsage ? normalizeWorkerUsage(data.workerUsage) : undefined),
    [data]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !usage) {
    return <PageLoading />;
  }

  if (!usage) {
    return <NotFoundPage />;
  }

  return <UsageWorkersNewContent usage={usage} />;
};

export default UsageWorkersNew;
